import { Component, Input, OnInit } from "@angular/core";
import { COMMON_IMPORTS } from "../../global-misc/common-imports";
import { TreatmentsViewFacade } from "./treatments-view.facade";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { AccordionModule } from "primeng/accordion";
import { MenuModule } from "primeng/menu";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { TreatmentWithAgeModel } from "./treatments-view.facade";
import { MenuItem } from "primeng/api";
import { CrudTreatmentComponent } from "../crud-treatment/crud-treatment.component";
import { CrudTreatmentMode } from "../crud-treatment/crud-treatment.facade";

@Component({
    selector: "app-treatments-view",
    standalone: true,
    imports: [...COMMON_IMPORTS, CardModule, TagModule, AccordionModule, MenuModule, DialogModule, ButtonModule, CrudTreatmentComponent],
    templateUrl: "./treatments-view.component.html",
    styleUrls: ["./treatments-view.component.css"],
})
export class TreatmentsViewComponent implements OnInit {
    @Input() patientId!: string;
    @Input() orgId!: string;
    @Input() collapsible: boolean = false;

    public accordionActiveIndex: number | null = null;

    public treatments: TreatmentWithAgeModel[] = [];
    public showUpdateTreatmentDialog: boolean = false;
    public selectedTreatmentId: string | undefined;
    public currentCrudTreatmentMode: CrudTreatmentMode = "update";

    public constructor(
        private readonly facade: TreatmentsViewFacade,
    ) { }

    public async ngOnInit() {

        await this.refreshTreatments();
    }

    public async refreshTreatments() {
        console.log("Refreshing treatments...");
        this.treatments = await this.facade.getTreatmentsWithAge(this.orgId, this.patientId);
        console.log(`Treatments refreshed. We now have ${this.treatments.length} treatments.`);
    }

    public getHumanReadableDate = this.facade.getHumanReadableDate;

    public getTreatmentStatus(treatment: TreatmentWithAgeModel): "success" | "warning" | "info" {
        if (!treatment.end_date) {
            return "success"; // Ongoing treatment
        }

        const endDate = new Date(treatment.end_date);
        const now = new Date();

        return endDate > now ? "info" : "warning"; // Future end date or completed
    }

    public getTreatmentStatusLabel(treatment: TreatmentWithAgeModel): string {
        if (!treatment.end_date) {
            return "Ongoing";
        }

        const endDate = new Date(treatment.end_date);
        const now = new Date();

        return endDate > now ? "In Progress" : "Completed";
    }

    public getTreatmentEndLabel(treatment: TreatmentWithAgeModel): string {
        if (!treatment.end_date) {
            return "Status";
        }

        const endDate = new Date(treatment.end_date);
        const now = new Date();

        return endDate > now ? "Ends" : "Ended";
    }

    public getLatestTreatment(): TreatmentWithAgeModel | null {
        if (this.treatments.length === 0) return null;

        return this.treatments.reduce((latest, current) => {
            return new Date(current.start_date) > new Date(latest.start_date) ? current : latest;
        });
    }

    public getActiveTreatments(): TreatmentWithAgeModel[] {
        return this.treatments.filter(treatment => !treatment.end_date || new Date(treatment.end_date) > new Date());
    }

    public calculateDuration(startDate: string, endDate: string): string {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) {
            return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return `${months} month${months === 1 ? '' : 's'}`;
        } else {
            const years = Math.floor(diffDays / 365);
            const remainingMonths = Math.floor((diffDays % 365) / 30);
            if (remainingMonths === 0) {
                return `${years} year${years === 1 ? '' : 's'}`;
            }
            return `${years} year${years === 1 ? '' : 's'}, ${remainingMonths} month${remainingMonths === 1 ? '' : 's'}`;
        }
    }

    public calculateDurationFromStart(startDate: string): string {
        const start = new Date(startDate);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) {
            return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return `${months} month${months === 1 ? '' : 's'}`;
        } else {
            const years = Math.floor(diffDays / 365);
            const remainingMonths = Math.floor((diffDays % 365) / 30);
            if (remainingMonths === 0) {
                return `${years} year${years === 1 ? '' : 's'}`;
            }
            return `${years} year${years === 1 ? '' : 's'}, ${remainingMonths} month${remainingMonths === 1 ? '' : 's'}`;
        }
    }

    public getTreatmentMenuItems(treatment: TreatmentWithAgeModel): MenuItem[] {
        return [
            {
                label: 'Update Treatment',
                icon: 'pi pi-pencil',
                command: () => this.openUpdateTreatmentDialog(treatment.id)
            },
            {
                label: 'End Treatment',
                icon: 'pi pi-stopwatch',
                command: () => this.endTreatment(treatment.id)
            },
            {
                label: 'Delete Treatment',
                icon: 'pi pi-times',
                command: () => this.deleteTreatment(treatment.id)
            }
        ];
    }

    public openUpdateTreatmentDialog(treatmentId: string): void {
        this.selectedTreatmentId = treatmentId;
        this.currentCrudTreatmentMode = "update";
        this.showUpdateTreatmentDialog = true;
    }

    public closeUpdateTreatmentDialog(): void {
        this.showUpdateTreatmentDialog = false;
        this.selectedTreatmentId = undefined;
    }

    public onTreatmentFormSubmit(): void {
        // Refresh treatments after successful update
        this.refreshTreatments();
        this.closeUpdateTreatmentDialog();
    }

    private endTreatment(treatmentId: string): void {
        /* just open update treatment dialog in end mode */
        this.selectedTreatmentId = treatmentId;
        this.currentCrudTreatmentMode = "end";
        this.showUpdateTreatmentDialog = true;
    }

    private async deleteTreatment(treatmentId: string) {
        await this.facade.deleteTreatment(this.orgId, this.patientId, treatmentId);
        this.refreshTreatments();
    }
}
